import { useEffect, useRef, useState } from 'react'
import styled, { useTheme, css } from 'styled-components';
import {
  isBefore,
  isAfter,
  startOfDay,
  getMonth,
  getYear,
  addYears,
  addDays,
  differenceInDays,
} from 'date-fns'
import { useDayzed } from 'dayzed'
import { useTranslation } from 'react-i18next'
import usePrevious from '../../../hooks/previous'
import useBreakpoints from '../../../hooks/breakpoints'
import Button from '../button'

const monthKeys = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
]
const weekdayKeys = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
]

const Container = styled.div`
  border: 1px solid ${props => props.theme.mainColor}
`;

const StyledButton = styled(Button)`
  ${props => props.hasBackground && css`
    background: ${props.theme.hoverColor} !important;
  `}
`;

const Day = styled.span`
  ${props => css`
    ${props.isDateSelected && css`
      background: ${props.theme.mainColor} !important;
      & > span {
        color: white !important;
      }
    `}
    ${props.isWithinRange && css`
      background: ${props.theme.hoverColor} !important;
      color: black;
    `}
    ${props.isDateSelectable && css`
      &:hover > span {
        border-color: ${props.theme.mainColor} !important;
      }
    `}
    ${!props.isDateSelectable && css`
      opacity: .5;
      cursor: not-allowed;
    `}
  `}

`;

const ChevronButton = styled(Button)`
  ${props => css`
    &:disabled > svg {
      fill: rgba(15,32,65,.2) !important;
    }
  `}
`;

function Calendar({ isOpen, selected, onDateSelected, minDate }) {
  const theme = useTheme();
  const { t, i18n } = useTranslation()
  const calendarRefs = useRef([])
  const containerRef = useRef()

  const [originalWidth, setWidth] = useState(280)

  const { isTablet, isMobile } = useBreakpoints()

  const width = isTablet ? originalWidth : originalWidth / 2
  const dayWidth = width / 7

  // We need to store this in state to only change it on open.
  // Otherwise Dayzed moves the calendar when selecting a date.
  const [openingDate, setOpeningDate] = useState()

  const firstDayOfWeek = i18n.language === 'en' ? 0 : 1

  let dayzedData = useDayzed({
    date: isTablet ? undefined : openingDate,
    selected,
    onDateSelected,
    firstDayOfWeek,
    minDate: minDate ? startOfDay(minDate) : addDays(startOfDay(new Date()), 2),
    maxDate: startOfDay(addYears(new Date(), 1)),
    monthsToDisplay: isMobile ? 1 : 2,
  })

  const { calendars, getDateProps, getBackProps, getForwardProps } = dayzedData

  const prevIsOpen = usePrevious(isOpen)

  useEffect(() => {
    if (
      isTablet &&
      !prevIsOpen &&
      isOpen &&
      ((Array.isArray(selected) && selected.length) || selected instanceof Date)
    ) {
      const month = Array.isArray(selected)
        ? getMonth(selected[0])
        : getMonth(selected)
      const year = Array.isArray(selected)
        ? getYear(selected[0])
        : getYear(selected)

      const index = calendars.findIndex(
        (calendar) => calendar.month === month && calendar.year === year
      )

      const calendarEl = calendarRefs.current[index]

      if (calendarEl && containerRef.current) {
        // Need the offset because of the fixed weekday header
        containerRef.current.scroll({ top: calendarEl.offsetTop - 50 })
      }
    }
  })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isTablet && !prevIsOpen && isOpen) {
      setOpeningDate(
        Array.isArray(selected) && selected.length
          ? selected[0]
          : selected instanceof Date
          ? selected
          : undefined
      )
    }
  })

  const calculateWidth = () => {
    if (containerRef.current) {
      const dimensions = containerRef.current.getBoundingClientRect()

      if (dimensions.width) {
        // 40 = 2 x side margin of 20px
        setWidth(dimensions.width > 1000 ? 960 : dimensions.width - 40)
      }
    }
  }

  useEffect(() => {
    calculateWidth()
  }, [])

  useEffect(() => {
    window.addEventListener('resize', calculateWidth)
    return () => {
      window.removeEventListener('resize', calculateWidth)
    }
  })

  if (!isOpen) {
    return null
  }

  if (calendars.length) {
    return (
      <Container
        theme={theme}
        ref={containerRef}
        className="ul-relative ul-w-[325px] md:ul-w-700 ul-bg-white ul-shadow-double ul-rounded-5 ul-z-10 ul-border">
        <div className="ul-mx-auto ul-pb-60 md:ul-pb-20 md:ul-flex">
          {calendars.map((calendar, index) => (
            <div key={`${calendar.month}${calendar.year}`} className="ul-w-full">
              {!isTablet || index === 0 ? (
                <div className="ul-relative ul-w-full ul-z-10 ul-left-1/2 ul-transform -ul-translate-x-1/2 ul-py-12 ul-border-b ul-border-dark ul-border-opacity-5 ul-text-center">
                  {Array(7)
                    .fill()
                    .map((_, index) => {
                      const weekdayIndex = (index + firstDayOfWeek) % 7
                      return (
                        <div
                          key={`${calendar.month}${calendar.year}${weekdayIndex}`}
                          style={{ width: dayWidth }}
                          className="ul-inline-block ul-text-12 ul-opacity-50 ul-text-center"
                        >
                          {t(
                            `components.datePicker.weekday.${weekdayKeys[weekdayIndex]}`
                          )}
                        </div>
                      )
                    })}
                </div>
              ) : null}
              <div
                ref={(el) => {
                  calendarRefs.current[index] = el
                }}
                style={{ maxWidth: width }}
                className={`${index === 0 ? 'md:ul-pt-0' : ''} ul-mx-auto`}
              >
                <div className="ul-mx-10 ul-mt-30 md:ul-mt-25 ul-mb-8 md:ul-mb-15 ul-relative">
                  <div className="ul-font-medium ul-text-22 md:ul-text-base md:ul-text-center">
                    {t(
                      `components.datePicker.months.${
                        monthKeys[calendar.month]
                      }`
                    )}{' '}
                    {calendar.year}
                  </div>
                  {index === 0 ? (
                    <ChevronButton
                      {...getBackProps({ calendars })}
                      unstyled
                      className={`ul-rounded-full ul-p-8 ul-border ul-border-dark disabled:ul-border-dark-20 ul-text-dark disabled:ul-text-dark-20 ul-mr-10 ul-absolute ul-top-1/2 -ul-translate-y-1/2 ul-transform ${isMobile && "ul-right-[30px]"}`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="ul-w-12 ul-transform ul-rotate-90">
                        <title>chevron</title>
                        <path d="M12,17.61,3.44,10.9A1.5,1.5,0,1,1,5.29,8.54L12,13.8l6.71-5.26a1.5,1.5,0,1,1,1.85,2.36Z" />
                      </svg>
                    </ChevronButton>
                  ) : null}
                  {isMobile || index === 1 ? (
                    <ChevronButton
                      {...getForwardProps({ calendars })}
                      unstyled
                      className="ul-rounded-full ul-p-8 ul-border ul-border-dark disabled:ul-border-dark-20 ul-text-dark disabled:ul-text-dark-20 ul-absolute ul-top-1/2 ul-right-0 -ul-translate-y-1/2 ul-transform"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="ul-w-12 ul-transform -ul-rotate-90">
                        <title>chevron</title>
                        <path d="M12,17.61,3.44,10.9A1.5,1.5,0,1,1,5.29,8.54L12,13.8l6.71-5.26a1.5,1.5,0,1,1,1.85,2.36Z" />
                      </svg>
                    </ChevronButton>
                  ) : null}
                </div>
                {calendar.weeks.map((week, weekIndex) => (
                  <div
                    className="ul-mt-2 ul-flex"
                    key={`${calendar.month}${calendar.year}${weekIndex}`}
                  >
                    {week.map((dateObj, index) => {
                      let key = `${calendar.month}${calendar.year}${weekIndex}${index}`

                      if (!dateObj) {
                        return (
                          <span
                            key={key}
                            style={{ width: dayWidth }}
                            className="ul-inline-block"
                          />
                        )
                      }

                      const {
                        date,
                        selected: isDateSelected,
                        selectable: isDateSelectable,
                      } = dateObj

                      const isWithinRange =
                        Array.isArray(selected) &&
                        selected[0] &&
                        isAfter(new Date(date), new Date(selected[0])) &&
                        selected[1] &&
                        isBefore(new Date(date), new Date(selected[1]))

                      const isFirstOfWeek =
                        index === 0 ||
                        date.getDate() === calendar.firstDayOfMonth.getDate()

                      const isLastOfWeek =
                        index === 6 ||
                        date.getDate() === calendar.lastDayOfMonth.getDate()

                      const isFirstSelected = 
                        isDateSelected &&
                        Array.isArray(selected) &&
                        selected?.length === 2 &&
                        date.getDate() === selected[0].getDate();
                      
                      const isLastSelected =
                        isDateSelected &&
                        Array.isArray(selected) &&
                        selected?.length === 2 &&
                        date.getDate() === selected[1].getDate();

                      return (
                        <StyledButton
                          hasBackground={isFirstSelected || isLastSelected}
                          className={`ul-inline-block ${
                            isFirstSelected
                              ? 'ul-rounded-l-full'
                              : isLastSelected
                                ? 'ul-rounded-r-full'
                                : ''
                          }`}
                          key={key}
                          {...getDateProps({ dateObj })}
                          unstyled
                        >
                          <Day
                            theme={theme}
                            isDateSelected={isDateSelected}
                            isDateSelectable={isDateSelectable}
                            isFirstOfWeek={isFirstOfWeek}
                            isLastOfWeek={isLastOfWeek}
                            isWithinRange={isWithinRange}
                            className={`${
                              isDateSelected
                                ? 'ul-text-white ul-rounded-full'
                                : isWithinRange && isFirstOfWeek && isLastOfWeek
                                ? 'ul-rounded-l ul-rounded-r'
                                : isWithinRange && isFirstOfWeek
                                ? 'ul-rounded-l'
                                : isWithinRange && isLastOfWeek
                                ? 'ul-rounded-r'
                                : isWithinRange
                                ? ''
                                : !isDateSelectable
                                ? 'ul-opacity-50 ul-cursor-not-allowed'
                                : ''
                            } ul-inline-block`}
                          >
                            <span
                              style={{ width: dayWidth, height: dayWidth }}
                              className={`ul-border-2 ul-border-transparent ul-rounded-full ul-inline-flex ul-items-center ul-justify-center ul-text-17`}
                            >
                              {date.getDate() < 10
                                ? `0${date.getDate()}`
                                : date.getDate()}
                            </span>
                          </Day>
                        </StyledButton>
                      )
                    })}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        {Array.isArray(selected) && selected.length === 2 ? (
          <div className="ul-text-right ul-pr-20 ul-py-8 ul-text-14 ul-text-dark-60 ul-border-t ul-border-dark-5 ul-hidden md:ul-block">
            {t('components.datePicker.days', {
              count: differenceInDays(selected[1], selected[0]) + 1,
            })}
          </div>
        ) : Array.isArray(selected) && selected.length === 1 ? (
          <div className="ul-text-right ul-pr-20 ul-py-8 ul-text-14 ul-text-dark-60 ul-border-t ul-border-dark-5 ul-hidden md:ul-block">
            {t('components.datePicker.chooseReturnDate')}
          </div>
        ) : null}
      </Container>
    )
  }
  return null
}

export default Calendar
